import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { EmployeeService } from './employee.service';
import { Service } from '../models/provider-user-info.model';
import { environment } from '../../environments/environment';
import { AdminUserType } from '@k12/models';
import { Observable, switchMap, map, catchError, of } from 'rxjs';
import { IUserRecordProviderDetailsService } from '@k12/admin-services';
import { Address } from '@k12/models';

@Injectable({
  providedIn: 'root'
})
export class ProviderRecordService implements IUserRecordProviderDetailsService {
  private readonly http: HttpClient = inject(HttpClient);
  private readonly employeeService: EmployeeService = inject(EmployeeService);
  public subServices: Service[] = [];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public getProviderDetails(userId: string, userType: AdminUserType = AdminUserType.Provider): Observable<any> {
    if (userType === AdminUserType.Provider) {
      return this.http.get(`${environment.adminApiUrl}/partners/providers/${userId}`).pipe(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        switchMap((userData: any) => {
          
          // Handle null or undefined userData
          if (!userData) {
            return of(null);
          }
          
          // Find the owner employee
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const ownerEmployee = userData.employees?.find((emp: any) => emp.isOwner === true);

          if (ownerEmployee) {
            // Fetch detailed owner information
            return this.employeeService.GetEmployeeDetails(ownerEmployee.id).pipe(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              map((ownerDetails: any) => ({
                ...userData,
                ownerDetails: ownerDetails
              })),
              catchError((error) => {
                console.error('Error fetching owner details:', error);
                return of(userData); // Return original data if owner details fail
              })
            );
          } else {
            // No owner found, return original user data
            return of(userData);
          }
        }),
        catchError((error) => {
          console.error('Error fetching user details:', error);
          return of(null);
        })
      );
    }
  
    return of(null);
  }

  parseProviderAddress(address: Address | null | undefined): string {
    if (!address) {
      return '';
    }

    let addressString = '';

    if (address.address1) {
      addressString += address.address1;
    }

    if (address.address2) {
      addressString += addressString ? '\n' + address.address2 : address.address2;
    }

    const cityStateZip: string[] = [];
    if (address.city) cityStateZip.push(address.city);
    let stateStr = '';
    if (address.state != null) {
      stateStr = typeof address.state === 'string' ? address.state : (address.state as any).name;
    }
    if (address.county) cityStateZip.push(address.county);
    if (stateStr) cityStateZip.push(stateStr);
    if (address.zip) cityStateZip.push(address.zip);

    if (cityStateZip.length > 0) {
      addressString += addressString ? '\n' + cityStateZip.join(', ') : cityStateZip.join(', ');
    }

    return addressString.replace(/\n/g, '<br>');
  }

   
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  parseServices(services: any[]): any[] {
    if (!services) {
      return [];
    }

    return services.map((service) => service.name);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getSubServices(employeeType?: number): Observable<any> {
    if (employeeType) {
      return this.http.get(
        `${environment.adminApiUrl}/partners/employees/subservices?employeeType=${employeeType}`
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ) as Observable<any>;
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return this.http.get(`${environment.adminApiUrl}/partners/employees/subservices`) as Observable<any>;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getAllProviders(): Observable<any> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.http.get(`${environment.adminApiUrl}/partners/providers`) as Observable<any>;
  }

   
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async editProviderDetails(payload: any): Promise<Observable<any>> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.http.put(`${environment.adminApiUrl}/partners/providers`, payload) as Observable<any>;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getProviderContacts(userId: string): Observable<any> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.http.get(`${environment.adminApiUrl}/schools/${userId}/hdmaContacts`) as Observable<any>;
  }
}
